import React from 'react';
import { Helmet } from 'react-helmet-async';
import { generateSEOMeta, generateStructuredData } from '../utils/seo';

interface SEOProps {
  pageType: string;
  service?: string;
  location?: string;
  customTitle?: string;
  customDescription?: string;
  customKeywords?: string;
  image?: string;
  url?: string;
}

const SEO: React.FC<SEOProps> = ({
  pageType,
  service,
  location,
  customTitle,
  customDescription,
  customKeywords,
  image = '/images/jaranets-logo.jpeg',
  url
}) => {
  const seoData = generateSEOMeta(pageType, service, location);
  const structuredData = generateStructuredData();
  
  const title = customTitle || seoData.title;
  const description = customDescription || seoData.description;
  const keywords = customKeywords || seoData.keywords;
  const canonicalUrl = url || `https://jaranets.com/${pageType}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="JaraNets Safety Solutions" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="JaraNets Safety Solutions" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#f59e0b" />
      
      {/* Language and Region */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="IN" />
      <meta name="geo.country" content="India" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Additional Structured Data for Services */}
      {service && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": service,
            "description": description,
            "provider": {
              "@type": "LocalBusiness",
              "name": "JaraNets Safety Solutions"
            },
            "areaServed": "India",
            "serviceType": "Safety Solutions"
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
